/**
 * Component-ID registry.
 *
 * The IAS framework token carries a `Roles[].Components[].ComponentId` list.
 * Each ComponentId is a 4-character opaque code that the framework admins
 * grant to users in the IAS portal.
 *
 * Role ComponentIds (must match backend handlers/middleware.py):
 *   ADMN – Super Admin   (can assign ideas, approve/close, manage users)
 *   MNGR – Manager       (can review, comment, rate, trigger AI)
 *   IOWN – Idea Owner    (can submit and track own ideas)
 *
 * Tool/stage ComponentIds gate individual features within the app.
 *
 * NOTE: `LEGACY_FULL` (`YDqe`) is the original full-app gate, kept so
 * existing users continue to work until new per-area components are provisioned.
 */

export const COMPONENT_IDS = Object.freeze({
  // ── Business roles ────────────────────────────────────────────────
  ROLE_ADMIN: 'ADMN',        // Super Admin
  ROLE_MANAGER: 'MNGR',      // Ideation Manager
  ROLE_IDEA_OWNER: 'IOWN',   // Idea Owner / submitter

  // Admin override — having this ComponentId on any role grants access to
  // every gated area in the app (system-level override).
  ADMIN: '0000',

  // Legacy full-app access (existing YDqe component)
  LEGACY_FULL: 'YDqe',

  // Legacy per-tool access (kept for the old top-level tools)
  IDEA_ANALYZER_LEGACY: 'YDqe',
  CODE_GENERATOR_LEGACY: 'CdGn',

  // SDLC workspace stages
  SDLC_IDEA: 'SdId',
  SDLC_REQUIREMENTS: 'SdRq',
  SDLC_ARCHITECTURE: 'SdAr',
  SDLC_CICD: 'SdCi',
  SDLC_CODE: 'SdCd',
  SDLC_TESTS: 'SdTs',
  SDLC_DEPLOYMENT: 'SdDp',
  SDLC_DOCS: 'SdDc',
});

/** All business-role ComponentIds (ADMN, MNGR, IOWN). */
export const ROLE_COMPONENT_IDS = Object.freeze([
  COMPONENT_IDS.ROLE_ADMIN,
  COMPONENT_IDS.ROLE_MANAGER,
  COMPONENT_IDS.ROLE_IDEA_OWNER,
]);

/** Maps each SDLC stage id (see components/sdlc/stages.js) → ComponentId. */
export const SDLC_STAGE_COMPONENT_IDS = Object.freeze({
  idea: COMPONENT_IDS.SDLC_IDEA,
  requirements: COMPONENT_IDS.SDLC_REQUIREMENTS,
  architecture: COMPONENT_IDS.SDLC_ARCHITECTURE,
  docs: COMPONENT_IDS.SDLC_DOCS,
  code: COMPONENT_IDS.SDLC_CODE,
  tests: COMPONENT_IDS.SDLC_TESTS,
  cicd: COMPONENT_IDS.SDLC_CICD,
  deployment: COMPONENT_IDS.SDLC_DEPLOYMENT,
});

/** Maps top-level tool id (see LeftNavPanel) → ComponentId(s). */
export const TOOL_COMPONENT_IDS = Object.freeze({
  // Idea Management is available to all three roles
  'idea-management': [
    COMPONENT_IDS.ROLE_ADMIN,
    COMPONENT_IDS.ROLE_MANAGER,
    COMPONENT_IDS.ROLE_IDEA_OWNER,
  ],
  // Admin panel: restricted to super admins
  'admin': [COMPONENT_IDS.ROLE_ADMIN, COMPONENT_IDS.ADMIN],
  // Legacy tools
  'idea-analyzer': [COMPONENT_IDS.IDEA_ANALYZER_LEGACY],
  'code-generator': [COMPONENT_IDS.CODE_GENERATOR_LEGACY, COMPONENT_IDS.LEGACY_FULL],
  // SDLC tool is visible if the user has access to ANY stage of it.
  sdlc: Object.values(SDLC_STAGE_COMPONENT_IDS),
});
